import type { DocumentEvidenceReference } from "@repo/shared/document-evidence-link";
import { type NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import type { ComponentPropsWithoutRef } from "react";
import { ReferenceChip } from "@/components/ui/reference-chip";
import {
  evidenceDocumentIcon,
  formatDiffEvidenceChipLabel,
  readEvidenceLabelFromAttrs,
  readEvidenceReferenceFromAttrs,
  UNAVAILABLE_DOCUMENT_EVIDENCE_TITLE,
  useDocumentEvidencePresentation,
} from "../../evidence";
import { readEvidenceRunPlacement } from "../plugins/document-evidence-runs";

function InlineNodeViewWrapper(props: ComponentPropsWithoutRef<"span">) {
  return <NodeViewWrapper as="span" {...props} />;
}

/**
 * `undefined` while the batched presentation is still loading, or when there is no
 * evidence provider at all. Only a resolved answer may claim the chip is unavailable —
 * otherwise every chip would flash the unavailable treatment on first paint.
 */
function resolveAvailability(
  reference: DocumentEvidenceReference | null,
  presentation: ReturnType<typeof useDocumentEvidencePresentation>
): "true" | "false" | undefined {
  if (!reference) return "false";
  if (presentation === undefined || presentation === null) return undefined;
  return presentation.status === "available" ? "true" : "false";
}

export function DocumentEvidenceNodeView({ decorations, node }: NodeViewProps) {
  const reference = readEvidenceReferenceFromAttrs(node.attrs);
  const presentation = useDocumentEvidencePresentation(reference);
  const label =
    presentation?.status === "available"
      ? presentation.documentName
      : readEvidenceLabelFromAttrs(node.attrs);
  const availability = resolveAvailability(reference, presentation);
  // Diff review must keep naming the citations a revision added or removed, so a
  // diff-marked node keeps the labeled chip; only live content collapses to a marker.
  const diffStatus = node.attrs._diffStatus ?? undefined;
  const placement = diffStatus ? null : readEvidenceRunPlacement(decorations);
  // The redline is the one place the label is visible prose, and it names where each
  // citation points so two citations of one document do not read as a duplicate; the
  // `data-evidence-label` below stays the bare document name the DOM readers expect.
  const visibleLabel =
    diffStatus && presentation?.status === "available"
      ? formatDiffEvidenceChipLabel(presentation)
      : label;

  return (
    <ReferenceChip
      as={InlineNodeViewWrapper}
      // The label is a `data-*` attribute and the accessible name in compact mode; the
      // visible text belongs to the popover, not the prose.
      label={diffStatus ? visibleLabel : null}
      icon={evidenceDocumentIcon({
        fileKind: presentation?.status === "available" ? presentation.fileKind : undefined,
        documentName: label,
      })}
      referenceKind="evidence"
      className={diffStatus ? undefined : "reference-chip--evidence-compact"}
      contentEditable={false}
      aria-label={diffStatus ? undefined : label}
      title={availability === "false" ? UNAVAILABLE_DOCUMENT_EVIDENCE_TITLE : undefined}
      data-type="documentEvidence"
      // Only the head of a run stays visible; the rest are hidden by CSS while remaining
      // in the document so selection, undo, diffing and the stored JSON are unaffected.
      data-evidence-run-role={placement ? (placement.isHead ? "head" : "continuation") : undefined}
      data-evidence-run-size={placement?.isHead ? String(placement.size) : undefined}
      data-evidence-label={label}
      data-evidence-available={availability}
      data-artifact-id={node.attrs.artifactId ?? undefined}
      data-processing-revision-id={node.attrs.processingRevisionId ?? undefined}
      data-block-id={node.attrs.blockId ?? undefined}
      data-start-char={node.attrs.startChar ?? undefined}
      data-end-char={node.attrs.endChar ?? undefined}
      data-diff-status={diffStatus}
    />
  );
}
